/**
 * MinistrareIA - Navigation Sidebar Component
 * Collapsible navigation with icon-only mode
 * 
 * UPDATED: Added channel switcher (Evolution vs WA Business)
 */
import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MessageSquare,
  Tags,
  Bot,
  GraduationCap,
  Bell,
  BellRing,
  User,
  Users,
  RefreshCw,
  Wifi,
  Sun,
  Moon,
  LogOut,
  PanelLeftOpen,
  Smartphone,
  Building2,
  ChevronDown,
  Check,
  ShoppingBag,
  Link2,
  LifeBuoy,
  CreditCard,
  HeartPulse,
  Activity,
  ShieldAlert,
} from 'lucide-react';

const DR_SALUS_USER_ID = '06445a4f624057';


const NavSidebar = ({ 
  currentPage, 
  onPageChange,
  onRefresh,
  onOpenConnection,
}) => {
  const { colors, theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChannelMenu, setShowChannelMenu] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Determine active channel from URL
  const activeChannel = location.pathname.startsWith('/wa-business') ? 'wa_business' : 'evolution';

  // Channel definitions
  const channels = [
    { 
      id: 'evolution', 
      label: 'WhatsApp Local', 
      description: 'Conexão via QR Code',
      icon: Smartphone, 
      color: '#25D366',
      path: '/inbox'
    },
    { 
      id: 'wa_business', 
      label: 'WhatsApp Business', 
      description: 'Conexão via Meta',
      icon: Building2, 
      color: '#1877f2',
      path: '/wa-business'
    },
  ];

  const currentChannelData = channels.find(c => c.id === activeChannel) || channels[0];

  const styles = {
    container: {
      width: '68px',
      minWidth: '68px',
      height: '100vh',
      backgroundColor: colors.bgPrimary,
      borderRight: `1px solid ${colors.borderColor}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: '12px',
    },
    // Channel Switcher
    channelSwitcher: {
      position: 'relative',
      marginBottom: '8px',
    },
    channelButton: {
      width: '46px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s',
      position: 'relative',
    },
    channelButtonInner: {
      width: '38px',
      height: '38px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    channelIndicator: {
      position: 'absolute',
      bottom: '2px',
      right: '2px',
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: colors.bgPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    channelMenu: {
      position: 'absolute',
      top: '0',
      left: '56px',
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      padding: '8px',
      zIndex: 1000,
      border: `1px solid ${colors.borderColor}`,
      minWidth: '220px',
    },
    channelMenuHeader: {
      padding: '8px 12px',
      fontSize: '11px',
      fontWeight: 600,
      color: colors.textTertiary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    channelMenuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      padding: '10px 12px',
      border: 'none',
      background: 'transparent',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      textAlign: 'left',
    },
    channelMenuItemActive: {
      backgroundColor: `${colors.accentPrimary}15`,
    },
    channelMenuItemHover: {
      backgroundColor: colors.bgTertiary,
    },
    channelMenuIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    channelMenuInfo: {
      flex: 1,
      minWidth: 0,
    },
    channelMenuLabel: {
      fontSize: '14px',
      fontWeight: 500,
      color: colors.textPrimary,
      marginBottom: '2px',
    },
    channelMenuDesc: {
      fontSize: '12px',
      color: colors.textSecondary,
    },
    channelMenuCheck: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    // Navigation
    nav: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      width: '100%',
      padding: '0 10px',
      marginTop: '8px',
    },
    navItem: {
      width: '46px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s',
      position: 'relative',
    },
    navItemActive: {
      backgroundColor: `${colors.accentPrimary}20`,
      color: colors.accentPrimary,
    },
    navItemHover: {
      backgroundColor: colors.bgTertiary,
    },
    navItemDot: {
      position: 'absolute',
      left: '0px',
      width: '4px',
      height: '20px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '0 4px 4px 0',
    },
    tooltip: {
      position: 'absolute',
      left: '60px',
      backgroundColor: colors.bgTertiary,
      color: colors.textPrimary,
      padding: '8px 12px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      zIndex: 1000,
      boxShadow: colors.shadowLg,
      pointerEvents: 'none',
    },
    divider: {
      width: '32px',
      height: '1px',
      backgroundColor: colors.borderColor,
      margin: '8px 0',
    },
    footer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      padding: '12px 10px',
      width: '100%',
    },
    footerBtn: {
      width: '46px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s',
      position: 'relative',
    },
    userAvatar: {
      width: '38px',
      height: '38px',
      borderRadius: '50%',
      backgroundColor: colors.bgTertiary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
      cursor: 'pointer',
      overflow: 'hidden',
      border: `2px solid transparent`,
      transition: 'border-color 0.15s',
    },
    userAvatarActive: {
      borderColor: colors.accentPrimary,
    },
    userMenu: {
      position: 'absolute',
      bottom: '60px',
      left: '68px',
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      boxShadow: colors.shadowLg,
      padding: '8px 0',
      zIndex: 1000,
      border: `1px solid ${colors.borderColor}`,
      minWidth: '200px',
    },
    userMenuHeader: {
      padding: '12px 16px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    userName: {
      fontSize: '14px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '2px',
    },
    userEmail: {
      fontSize: '12px',
      color: colors.textSecondary,
    },
    userMenuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      padding: '12px 16px',
      border: 'none',
      background: 'transparent',
      color: colors.textPrimary,
      fontSize: '14px',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background-color 0.15s',
    },
    userMenuItemDanger: {
      color: '#ef4444',
    },
    userMenuDivider: {
      height: '1px',
      backgroundColor: colors.borderColor,
      margin: '8px 0',
    },
  };

  const isDrSalus = user?.id === DR_SALUS_USER_ID;

  const navItems = [
    { id: 'inbox', label: 'Conversas', icon: MessageSquare },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'followups', label: 'Follow-ups', icon: Bell },
    ...(user?.default_channel === 'wa_business' ? [
      { id: 'templates', label: 'Templates', icon: Tags },
      { id: 'reminders', label: 'Lembretes', icon: BellRing },
      { id: 'tickets', label: 'Tickets', icon: LifeBuoy },
    ] : []),
    ...(isDrSalus ? [
      { id: 'plans', label: 'Planos Dr. Salus', icon: CreditCard },
      { id: 'subscribers', label: 'Assinantes Dr. Salus', icon: HeartPulse },
      { id: 'control-panel', label: 'Painel de Controle', icon: Activity },
      { id: 'safety-monitor', label: 'Monitor de Segurança', icon: ShieldAlert },
    ] : []),
    { id: 'products', label: 'Produtos', icon: ShoppingBag },
    { id: 'video-links', label: 'Video Links', icon: Link2 },
    { id: 'agents', label: 'Agentes', icon: Bot },
  ];

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserMenu && !e.target.closest('.nav-user-container')) {
        setShowUserMenu(false);
      }
      if (showChannelMenu && !e.target.closest('.nav-channel-container')) {
        setShowChannelMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu, showChannelMenu]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRefresh = () => {
    setShowUserMenu(false);
    onRefresh?.();
  };

  const handleOpenConnection = () => {
    setShowUserMenu(false);
    onOpenConnection?.();
  };

  const handleToggleTheme = () => {
    toggleTheme();
  };

  return (
    <div style={styles.container}>

      {/* Navigation */}
      <nav style={styles.nav}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.id;
          const isHovered = hoveredItem === item.id;

          return (
            <button
              key={item.id}
              style={{
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {}),
                ...(!isActive && isHovered ? styles.navItemHover : {}),
              }}
              onClick={() => onPageChange(item.id)}
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {isActive && <div style={styles.navItemDot} />}
              <Icon size={22} />
              {isHovered && (
                <div style={styles.tooltip}>{item.label}</div>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.divider} />
        
        {/* Theme Toggle */}
        <button
          style={{
            ...styles.footerBtn,
            ...(hoveredItem === 'theme' ? styles.navItemHover : {}),
          }}
          onClick={handleToggleTheme}
          onMouseEnter={() => setHoveredItem('theme')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          {hoveredItem === 'theme' && (
            <div style={styles.tooltip}>
              {theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
            </div>
          )}
        </button>

        {/* Mobile Menu Expand */}
        <button
          style={{
            ...styles.footerBtn,
            ...(hoveredItem === 'expand' ? styles.navItemHover : {}),
            display: 'none',
          }}
          className="mobile-expand-btn"
          onMouseEnter={() => setHoveredItem('expand')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <PanelLeftOpen size={20} />
          {hoveredItem === 'expand' && (
            <div style={styles.tooltip}>Expandir menu</div>
          )}
        </button>

        {/* User Avatar */}
        <div style={{ position: 'relative' }} className="nav-user-container">
          <div
            style={{
              ...styles.userAvatar,
              ...(showUserMenu ? styles.userAvatarActive : {}),
            }}
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <User size={18} />
          </div>

          {showUserMenu && (
            <div style={styles.userMenu}>
              <div style={styles.userMenuHeader}>
                <div style={styles.userName}>{user?.name || 'Usuário'}</div>
                <div style={styles.userEmail}>{user?.email}</div>
              </div>
              <button style={styles.userMenuItem} onClick={handleRefresh}>
                <RefreshCw size={16} />
                Atualizar conversas
              </button>
              <button style={styles.userMenuItem} onClick={handleOpenConnection}>
                <Wifi size={16} />
                Conexão WhatsApp
              </button>
              <div style={styles.userMenuDivider} />
              <button
                style={{ ...styles.userMenuItem, ...styles.userMenuItemDanger }}
                onClick={handleLogout}
              >
                <LogOut size={16} />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CSS for mobile */}
      <style>{`
        @media (max-width: 768px) {
          .mobile-expand-btn {
            display: flex !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NavSidebar;